import {
  createContext,
  forwardRef,
  useContext,
  type ComponentPropsWithoutRef,
  type ComponentType,
  type CSSProperties,
  type ElementRef,
  type ElementType,
  type ForwardedRef,
  type ReactElement,
  type RefAttributes,
} from 'react';

import { twMerge } from '@/domains/core/tamagoshiTailwind/util/twMerge';

export type LinkListItemColor = 'dark' | 'light' | 'none';

export const LinkListContext = createContext<LinkListItemColor | undefined>(
  undefined,
);

interface LinkListItemBaseProps<AnchorElement extends ElementType> {
  anchorTag?: AnchorElement;
  className?: string;
  color?: LinkListItemColor;
  styles?: CSSProperties;
  tag?: keyof JSX.IntrinsicElements;
}

/** Props that won't be forwarded to AnchorTag element. */
type LinkListItemOwnProps = 'anchorTag' | 'className' | 'color' | 'tag';

export type LinkListItemProps<AnchorElement extends ElementType> =
  LinkListItemBaseProps<AnchorElement> &
    Omit<ComponentPropsWithoutRef<AnchorElement>, LinkListItemOwnProps>;

/**
 * LinkListItem are used for complementary information such as "read more" and
 * external links.
 *
 * They are also used in lists of links and minor actions like download files.
 *
 * You should use simple links when you have to deal with multiple no-primary actions.
 */
function LinkListItemComponent<AnchorElement extends ElementType>(
  {
    anchorTag: AnchorTag = 'a' as AnchorElement,
    className,
    color,
    style,
    tag: Tag = 'li',
    ...restProps
  }: LinkListItemProps<AnchorElement>,
  ref: ForwardedRef<AnchorElement>,
): ReactElement {
  const sharedColor = useContext(LinkListContext);
  const listItemColor = color || sharedColor || 'none';

  return (
    <Tag
      className={twMerge(
        listItemColor === 'dark' && 'text-foundation-inverted',
        listItemColor === 'light' && 'text-foundation-primary',
        className,
      )}
      style={style}
    >
      <AnchorTag
        ref={ref}
        tabIndex={0}
        className="color-inherit visited:color-inherit w-full text-body2
          font-regular leading-tiny no-underline outline-none
          hover:cursor-pointer hover:underline focus:underline active:underline"
        {...(restProps as any)}
      />
    </Tag>
  );
}

export const LinkListItem = forwardRef(LinkListItemComponent) as <
  AnchorElement extends ElementType = 'a',
>(
  props: LinkListItemProps<AnchorElement> &
    RefAttributes<ElementRef<AnchorElement>>,
) => ReactElement;

(LinkListItem as ComponentType).displayName = 'LinkListItem';
